import axios, { AxiosError } from 'axios';
import { getSessionBootstrapUrl, isInIframe } from '@/utils';

// Use the build-time defined BASE_URL from Vite's define config
// Vite replaces __BASE_URL__ at build time with the value from vite.config.mjs
// which reads from VITE_API_BASE_URL env variable or defaults to app.mingava.se

// Declare the global that Vite will replace at build time
declare const __BASE_URL__: string;

export const BASE_URL = __BASE_URL__;


export const MainApi = 'api';

export const ENDPOINTS = {
  ADMIN_FEE: 'adminfee',
  CERTIFICATE: 'certificate',
  CODE: 'code',
  COOKIES: 'cookies',
  DOCUMENT: 'document',
  DONATION: 'donation',
  DONATION_CAUSES: 'donationcauses',
  DONATION_TYPES: 'donationtypes',
  CERTIFICATE_IMAGE: 'donation/certificate-image',
  DONATIONS: 'donations',
  ERROR: 'error',
  FILES: 'files',
  FINALIZE: 'finalize',
  IMAGE_CATEGORIES: 'image-categories',
  IMAGES: 'images',
  INITIAL_DONATION_CAUSES: '/v2/donation-causes',
  INVOICE: 'invoice',
  LATEST: 'latest',
  OPERATIONS: 'operations',
  PAY_REQUEST: 'payrequest',
  PAYMENT_METHODS: 'paymentmethods',
  PAYMENTS: 'payments',
  PRINT: 'print',
  RECIPIENTS: 'recipients',
  RECEIPT: 'receipt',
  SEARCH: 'search',
  SEND: 'send',
  SERIALNUMBER: 'serialnumber',
  SESSIONS_COOKIE_REQUEST: 'sessions/cookie/request',
  SESSIONS_REQUEST: 'sessions/request',
  SMS: 'sms',
  SPAR_SERVICE: 'service/spar',
  STATUS: 'status',
  SYSTEM_STATUS: 'system/status',
  SYSTEM_BANNERS: 'system/banners',
  TOKEN: 'token',
  TRANSACTIONS: 'transactions',
  TRANSLATIONS: 'translations',
  USER_LOCATION: 'locations/country',
  AS_JSON: 'asjson',
};

export const ERROR_PAGE_URL = `${BASE_URL}/${ENDPOINTS.ERROR}`;

export function getErrorPageUrl(): string {
  if (isInIframe()) {
    return `${ERROR_PAGE_URL}?fullpath=${encodeURIComponent(getSessionBootstrapUrl())}`;
  }
  return ERROR_PAGE_URL;
}

const api = axios.create({
  baseURL: `${BASE_URL}/${MainApi}`,
  withCredentials: true,
});

export function guideErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

export function guideHttpStatus(error: unknown): number | undefined {
  if (axios.isAxiosError(error)) {
    return error.response?.status;
  }
  return undefined;
}

function apiPathForLog(url: string | undefined): string | undefined {
  if (!url) {
    return undefined;
  }
  const path = url.includes('/api/') ? url.split('/api/')[1] : url.replace(`${BASE_URL}/${MainApi}/`, '');
  if (path.includes('service/spar')) {
    return 'service/spar/[redacted]';
  }
  return path.split('?')[0];
}

function logApiFailure(error: AxiosError) {
  const status = error.response?.status;
  if (status === 401) {
    return;
  }
  window.log?.error('[Guide] api.request — API request failed', {
    message: guideErrorMessage(error),
    httpStatus: status,
    apiPath: apiPathForLog(error.config?.url),
    method: error.config?.method?.toUpperCase(),
  });
}

export async function get(endpoint: string, params?: object, doHandleError = true) {
  try {
    const response = await api.get(endpoint, { params });
    return response.data;
  } catch (error) {
    if (doHandleError) handleError(error as AxiosError);
    return null;
  }
}

export async function deleteRequest(endpoint: string, params?: object, doHandleError = true) {
  try {
    const response = await api.delete(endpoint, { params });
    return response.data;
  } catch (error) {
    if (doHandleError) handleError(error as AxiosError);
    throw error;
  }
}

export async function post(endpoint: string, params: object, doHandleError = true) {
  try {
    const response = await api.post(endpoint, params);
    return response.data;
  } catch (error) {
    if (doHandleError) handleError(error as AxiosError);
    throw error;
  }
}

export async function postFile(endpoint: string, formData: FormData, params?: object, doHandleError = true) {
  try {
    const response = await api.post(endpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params,
    });
    return response.data;
  } catch (error) {
    if (doHandleError) handleError(error as AxiosError);
    throw error;
  }
}

export async function put(endpoint: string, params: object, doHandleError = true) {
  try {
    const response = await api.put(endpoint, params);
    return response.data;
  } catch (error) {
    if (doHandleError) handleError(error as AxiosError);
    throw error;
  }
}

export async function getFromSpar(ssn: string) {
  try {
    const response = await axios.get(`${BASE_URL}/${ENDPOINTS.SPAR_SERVICE}/${ssn}`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    handleError(error as AxiosError);
    throw error;
  }
}

export async function deleteCookie(cookieName: string, doHandleError = true) {
  try {
    const response = await api.delete(`/cookies/${cookieName}`);
    return response.data;
  } catch (error) {
    if (doHandleError) handleError(error as AxiosError);
    throw error;
  }
}

function handleError(error: AxiosError) {
  logApiFailure(error);
  if (error.response?.status === 401) {
    location.href = getErrorPageUrl();
  }
  throw error;
}
